// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import HeaderSearchClickable from "@werner/App/Components/Common/HeaderSearchClickable";
import XngZone from "@werner/App/Components/Common/XngZone";
import xHeaderUpdate from "@werner/App/Handlers/XNG/Header";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import { CartLinkZoneName, QuickOrderZoneName, SignInZoneName } from "@werner/XDL/constants";
import { xScreen } from "@werner/XDL/Utilities";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    return {
        isAuthenticated: xIsAuthenticated(state),
    };
};

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

interface HeaderActionsStyle {
    wrapper?: InjectableCss;
}

const styles: HeaderActionsStyle = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 20px;
            ${xScreen("lte", "md")} {
                gap: 24px;
            }
            ${xScreen("lte", "sm")} {
                gap: 24px;
            }
            &.cms-mode {
                flex-direction: column;
            }
            > div:empty {
                display: none;
            }
        `,
    },
};

const HeaderActions = ({ id, isAuthenticated }: Props) => {
    const { isEditing: isEditingInShell } = useShellContext();
    const dispatch = useAppDispatch();
    const { isMobileScreen, showSearchSectionOnMobile } = useAppSelector(state => ({
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
        showSearchSectionOnMobile: (state as CustomAppState).xHeader.showSearchSectionOnMobile,
    }));

    const toggleSearch = () => {
        dispatch(xHeaderUpdate({ key: "showSearchSectionOnMobile", value: !showSearchSectionOnMobile }));
    };

    return (
        <StyledWrapper {...styles.wrapper} className={isEditingInShell ? "cms-mode" : ""}>
            {isMobileScreen && <HeaderSearchClickable isShow={showSearchSectionOnMobile} onClick={toggleSearch} />}
            {(isAuthenticated || isEditingInShell) && (
                <XngZone contentId={id} zoneName={QuickOrderZoneName} hint="Quick Order" />
            )}
            <XngZone contentId={id} zoneName={SignInZoneName} hint="Common/HeaderSignIn" />
            <XngZone contentId={id} zoneName={CartLinkZoneName} hint="Common/CartLink" />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<FieldDefinition> = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(HeaderActions)),
    definition: {
        group: "Xngage Custom" as any,
    },
};

export default widgetModule;
